import { useEffect, useRef, useState } from 'react'

import { useNavigate, useSearch } from '@tanstack/react-router'
import { Trans, useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@chakra-ui/react'

import {
  Link,
  SideMenuLayoutBreadcrumb,
  SideMenuLayoutBreadcrumbItem,
  SideMenuLayoutContentLayout,
} from '@enechain/ecloud-designsystem'

import { EmailVerifiedResponse } from '~/gen/proto/bff/v1/common_pb'
import { EmailVerifiedStatus, Language } from '~/gen/proto/bff/v1/model_pb'
import { useLoginUser } from '~/hooks/useLoginUser'
import { EmailVerifiedAlert } from '~/pages/Home/components/EmailVerifiedAlert'
import { ProductLink } from '~/pages/Home/components/ProductLink'
import { useListProducts } from '~/pages/Home/hooks/useProduct'
import { Route } from '~/routes/_authenticated'

import { useEmailVerified } from './hooks/useEmailVerified'

export const Home: React.FC = () => {
  const { t } = useTranslation()
  const me = useLoginUser()
  const products = useListProducts()
  const breadcrumbItems: SideMenuLayoutBreadcrumbItem[] = [
    {
      label: t('pages.home.title'),
      bold: true,
    },
  ]
  const [emailVerifiedStatus, setEmailVerifiedStatus] = useState(
    EmailVerifiedStatus.UNKNOWN,
  )
  const navigate = useNavigate({ from: Route.fullPath })
  const { mutate: emailVerified } = useEmailVerified(
    (data: EmailVerifiedResponse) => {
      setEmailVerifiedStatus(data.status)
      navigate({ replace: true }).catch((e) => console.error(e))
    },
  )
  const params = useSearch({ from: '/_authenticated/' })
  const ref = useRef(true)

  useEffect(() => {
    if (Object.hasOwn(params, 'email-verify') && ref.current) {
      ref.current = false
      emailVerified({ id: params['email-verify'] })
    }
  }, [params['email-verify']])

  return (
    <SideMenuLayoutContentLayout
      header={
        <Flex alignItems="center" justifyContent="space-between" width="full">
          <SideMenuLayoutBreadcrumb items={breadcrumbItems} />
        </Flex>
      }
    >
      <Flex
        flexDirection="column"
        gap="3"
        width="960px"
        maxWidth="90%"
        margin="auto"
      >
        <EmailVerifiedAlert status={emailVerifiedStatus} />
        <Text textStyle="headerMd" color="Text.highEmphasis" textAlign="left">
          {t('pages.home.subtitle')}
        </Text>
        <Flex flexWrap="wrap" gap="6">
          {products.length > 0 ? (
            products
              .filter((p) => p.isPublic)
              .map((p) => {
                return <ProductLink key={p.name} name={p.name} url={p.url} />
              })
          ) : (
            <Box
              width="100%"
              padding="4"
              color="Text.neutralMid"
              backgroundColor="Surface.neutralLow"
            >
              <Trans
                components={{
                  Inquiry: (
                    <Link
                      href={
                        me.language === Language.ENGLISH
                          ? import.meta.env.VITE_ENG_INQUIRY_FORM_URL
                          : import.meta.env.VITE_INQUIRY_FORM_URL
                      }
                    />
                  ),
                }}
                i18nKey="pages.home.description.noProduct"
              />
            </Box>
          )}
        </Flex>
      </Flex>
    </SideMenuLayoutContentLayout>
  )
}
